/* Sticker Distortion M6 Phase E v4.6 — gated project cart client.
 * Adapted from Vyzzor pattern: bearer cart, idempotent writes, server quotes,
 * immutable artwork references. NEVER calls Shopify native /cart/add.js.
 * Checkout is NOT implemented. Enabled only through the section editor.
 */
(() => {
  'use strict';
  const API_ROUTE = '/?sd_route=cart/';
  const KEY = 'sd_m6_cart_id_v1';
  const CART_PATTERN = /^SDC_[A-Za-z0-9_-]{32,96}$/;
  const ART_PATTERN = /^[A-Za-z0-9_-]{32,128}$/;
  const DEFAULT_MESSAGE = 'Upload your original artwork before adding a project.';
  const stateByRoot = new WeakMap();

  function readId() {
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        const id = window[name].getItem(KEY);
        if (CART_PATTERN.test(id || '')) return id;
      } catch (_) { /* preview browsers can block storage */ }
    }
    return null;
  }
  function saveId(id) {
    if (!CART_PATTERN.test(id || '')) return false;
    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name].setItem(KEY, id); return true; }
      catch (_) { /* continue to safe fallback */ }
    }
    return false;
  }
  function forgetId() {
    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name].removeItem(KEY); } catch (_) { /* storage may be unavailable */ }
    }
  }
  function randomKey() {
    if (!window.crypto || !window.crypto.getRandomValues) throw new Error('Secure browser randomness is required.');
    const bytes = new Uint8Array(24);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  function showMoney(cents) {
    const amount = Number(cents);
    if (!Number.isSafeInteger(amount) || amount < 0) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount / 100);
  }
  function validQuote(details) {
    const d = details || {}, c = d.configuration || {}, q = d.quote || {};
    const allowed = ['pricing_key', 'width', 'height', 'quantity', 'shape', 'cut_style', 'finish'];
    return q.status === 'priced' && q.quote_required !== true &&
      Number.isFinite(Number(q.total_price)) && Number(q.total_price) > 0 &&
      typeof c.pricing_key === 'string' && allowed.every(k => c[k] !== undefined && c[k] !== null)
      ? Object.fromEntries(allowed.map(k => [k, c[k]])) : null;
  }

  function install(root) {
    if (stateByRoot.has(root)) return;
    const panel = root.querySelector('[data-sd-m6-cart]');
    if (!panel) return;
    const button = root.querySelector('[data-sd-m6-add]');
    const status = root.querySelector('[data-sd-m6-cart-status]');
    const items = panel.querySelector('[data-sd-m6-cart-items]');
    const subtotal = panel.querySelector('[data-sd-m6-cart-subtotal]');
    const count = panel.querySelector('[data-sd-m6-cart-count]');
    const restore = panel.querySelector('[data-sd-m6-cart-restore]');
    if (![button, status, items, subtotal, count, restore].every(Boolean)) return;
    // Theme switch must be explicit and backend cannot be changed from the browser.
    if (panel.dataset.pilot !== 'true') return;
    const uploadOn = panel.dataset.uploadEnabled === 'true';
    const api = String(root.dataset.apiBase || '').replace(/\/+$/, '') + API_ROUTE;
    const state = { quote: null, busy: false, cart: null, pending: null, uploadOn };
    stateByRoot.set(root, state);
    const statusText = (message, failed = false) => {
      status.textContent = message;
      status.dataset.error = failed ? 'true' : 'false';
    };
    const token = () => {
      const value = root.querySelector('[data-sd-artwork-token-input]')?.value || '';
      return ART_PATTERN.test(value) && root.dataset.sdArtworkToken === value ? value : null;
    };
    // Version-bound customer artwork approval. This UI gate is not server audit evidence.
    const approvalInput = root.querySelector('[data-sd-print-approval]');
    const approvalStatus = root.querySelector('[data-sd-print-approval-status]');
    let approvalStamp = null;
    const approvalFingerprint = () => state.quote && token()
      ? JSON.stringify({ artwork_token: token(), configuration: state.quote }) : null;
    function clearApproval() {
      approvalStamp = null;
      if (approvalInput) approvalInput.checked = false;
      root.dataset.sdPrintApproved = 'false';
    }
    function approvalReady() {
      const readyForConsent = uploadOn && root.dataset.sdCartEditBlocked !== 'true' &&
        root.dataset.sdStudioNeedsSave !== 'true' && Boolean(state.quote) && Boolean(token());
      const fingerprint = approvalFingerprint();
      if (!readyForConsent || (approvalInput?.checked && fingerprint !== approvalStamp)) clearApproval();
      if (approvalInput) approvalInput.disabled = !readyForConsent || state.busy;
      const valid = Boolean(readyForConsent && approvalInput?.checked && fingerprint && approvalStamp === fingerprint);
      root.dataset.sdPrintApproved = valid ? 'true' : 'false';
      if (approvalStatus) approvalStatus.textContent = !uploadOn ? 'Artwork upload is disabled.' :
        root.dataset.sdStudioNeedsSave === 'true' ? 'Save the edited artwork before approval.' :
        !token() ? 'Upload your artwork before approval.' :
        !state.quote ? 'Choose sticker specifications and wait for the price.' :
        valid ? 'Artwork and sticker options approved.' :
        'Review the saved artwork and specifications, then check the box.';
      return valid;
    }
    const canAdd = () => !state.busy && root.dataset.sdCartEditBlocked !== 'true' &&
      uploadOn && root.dataset.sdStudioNeedsSave !== 'true' && Boolean(state.quote) &&
      Boolean(token()) && approvalReady();
    function sync() {
      approvalReady();
      button.disabled = !canAdd();
      button.setAttribute('aria-disabled', String(button.disabled));
    }
    function render(cart) {
      state.cart = cart;
      items.replaceChildren();
      const list = Array.isArray(cart?.items) ? cart.items : [];
      count.textContent = String(list.length);
      subtotal.textContent = showMoney(cart?.subtotal_cents);
      for (const line of list) {
        const itemId = line?.item_id;
        if (typeof itemId !== 'string' || !/^SDI_[A-Za-z0-9_-]{24,96}$/.test(itemId)) continue;
        const row = document.createElement('li');
        row.className = 'sd-m6-cart__item';
        const info = document.createElement('div');
        const heading = document.createElement('strong');
        heading.textContent = `${String(line.shape || '').replace(/_/g, ' ').toUpperCase()} · ${line.width} × ${line.height} IN · ${line.sticker_quantity} PCS`;
        const details = document.createElement('span');
        details.textContent = `${String(line.cut_style || '').replace(/_/g, ' ')} / ${String(line.finish || '')} · ${String(line.artwork_filename || '').slice(0, 100)}`;
        info.append(heading, details);
        const right = document.createElement('div');
        right.className = 'sd-m6-cart__item-actions';
        const price = document.createElement('strong');
        price.textContent = showMoney(line.quoted_cents);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = 'REMOVE';
        remove.className = 'sd-m6-cart__remove';
        remove.disabled = state.busy;
        remove.addEventListener('click', () => removeProject(itemId));
        right.append(price, remove);
        row.append(info, right);
        items.append(row);
      }
    }
    async function request(action, payload) {
      const res = await fetch(api + action, {
        method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store',
        referrerPolicy: 'no-referrer', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      let body;
      try { body = await res.json(); } catch (_) { throw new Error('Cart service returned an unreadable response.'); }
      if (!res.ok || body?.ok !== true || !body?.cart) {
        const error = new Error(body?.message || 'Cart service is unavailable.');
        error.code = body?.error || 'cart_unavailable';
        error.httpStatus = res.status;
        throw error;
      }
      return body.cart;
    }
    async function restoreCart() {
      const id = readId();
      if (!id) return null;
      try {
        const cart = await request('get', { cart_id: id });
        render(cart);
        return cart;
      } catch (error) {
        if (error.httpStatus === 404 || error.httpStatus === 410) {
          forgetId();
          state.cart = null;
          items.replaceChildren();
          count.textContent = '0';
          subtotal.textContent = showMoney(0);
          return null;
        }
        throw error;
      }
    }
    async function getOrCreateCart() {
      let cart = state.cart;
      if (cart?.cart_id && CART_PATTERN.test(cart.cart_id)) return cart;
      cart = await restoreCart();
      if (cart) return cart;
      cart = await request('new', {});
      if (!CART_PATTERN.test(cart.cart_id || '')) throw new Error('Invalid cart reference returned by server.');
      saveId(cart.cart_id); // Persist after creation, before the add request.
      render(cart);
      return cart;
    }
    async function addProject() {
      if (!canAdd()) return;
      const selectedToken = token();
      const payload = { ...state.quote, artwork_token: selectedToken, instructions: '', print_approval_accepted: approvalReady() === true };
      const fingerprint = JSON.stringify(payload);
      state.busy = true;
      sync();
      statusText('Saving project to your private cart…');
      try {
        const editItem = root.dataset.sdCartEditItem;
        const editVersion = Number(root.dataset.sdCartEditVersion);
        const editMode= /^SDI_[A-Za-z0-9_-]{24,96}$/.test(editItem||'') && Number.isInteger(editVersion) && editVersion>=0;
        // Editing MUST NEVER silently create a new cart or duplicate the item.
        const cart = editMode ? await restoreCart() : await getOrCreateCart();
        if (!cart || (editMode && (!cart.items.some(x=>x.item_id===editItem)|| (cart.version!==editVersion && !(state.pending?.fingerprint===fingerprint && state.pending?.cart_id===cart.cart_id))))) {
          throw new Error('The cart changed since this project was opened. Return to Your Cart and refresh before editing.');
        }
        if (!state.pending || state.pending.fingerprint !== fingerprint || state.pending.cart_id !== cart.cart_id) {
          state.pending = { fingerprint, cart_id: cart.cart_id, nonce: randomKey() };
        }
        const updated = editMode ? await request('replace', {
          cart_id:cart.cart_id,item_id:editItem,project:payload,
          idempotency_key:state.pending.nonce,expected_version:editVersion
        }) : await request('add', {
          cart_id: cart.cart_id, project: payload, idempotency_key: state.pending.nonce
        });
        state.pending = null;
        render(updated);
        root.dispatchEvent(new CustomEvent('sd:m6-cart-updated', { bubbles: true, detail: { action: editMode?'replace':'add' } }));
        if(editMode){
          try{sessionStorage.removeItem('sd_cart_edit_pending_v1');}catch(_){}
          delete root.dataset.sdCartEditItem;delete root.dataset.sdCartEditVersion;
        }
        statusText(editMode?'Project updated in your cart.':'Project added to your cart.');
        if (readId()===updated.cart_id && window.location?.assign) { window.location.assign('/pages/project-cart'); }
      } catch (error) {
        // Retain pending idempotency key on uncertain network failure; retries will
        // not create duplicate items if the server already applied this request.
        statusText(error.code === 'cart_disabled' ?
          'Cart API is disabled. Your file and selections have not been added.' :
          (error.message || 'Project was not saved; please try again.'), true);
      } finally { state.busy = false; sync(); }
    }
    async function removeProject(itemId) {
      if (state.busy || !state.cart || !CART_PATTERN.test(state.cart.cart_id || '')) return;
      state.busy = true; sync();
      statusText('Removing project from cart (original artwork is retained)…');
      try {
        const cart = await request('remove', {
          cart_id: state.cart.cart_id, item_id: itemId,
          expected_version: state.cart.version, idempotency_key: randomKey()
        });
        render(cart);
        root.dispatchEvent(new CustomEvent('sd:m6-cart-updated', { bubbles: true, detail: { action: 'remove' } }));
        statusText('Project removed. Original artwork remains stored on the server.');
      } catch (error) {
        statusText(error.message || 'Unable to remove project. Refresh your cart.', true);
      } finally { state.busy = false; sync(); }
    }
    root.addEventListener('sd:cart-edit-mode',()=>{
      clearApproval();
      button.textContent='UPDATE CART';sync();
      statusText('Editing a saved project. UPDATE CART replaces this item, not adds another.');
    });
    if (approvalInput) approvalInput.addEventListener('change', () => {
      if (root.dataset.sdGeometryCaptureEnabled === 'true' && approvalInput.checked) {
        const quoteAtStart = JSON.stringify(state.quote);
        clearApproval(); sync();
        if (approvalStatus) approvalStatus.textContent = 'Saving the exact cutline for production review…';
        (async () => {
          try {
            const ok = await root.sdEnsureProductionCapture?.();
            if (ok && JSON.stringify(state.quote) === quoteAtStart && token() &&
                root.dataset.sdStudioNeedsSave !== 'true' &&
                root.dataset.sdCartEditBlocked !== 'true') {
              approvalInput.checked = true;
              approvalStamp = approvalFingerprint();
            } else if (approvalStatus) {
              approvalStatus.textContent = 'Production geometry could not be captured. Please save and retry.';
            }
          } catch {
            if (approvalStatus) approvalStatus.textContent = 'Could not capture the approved cutline.';
          } finally { sync(); }
        })();
        return;
      }
      if (approvalInput.checked && uploadOn && state.quote && token() &&
          root.dataset.sdStudioNeedsSave !== 'true' && root.dataset.sdCartEditBlocked !== 'true') {
        approvalStamp = approvalFingerprint();
      } else clearApproval();
      sync();
    });
    button.addEventListener('click', addProject);
    restore.addEventListener('click', async () => {
      if (state.busy) return;
      state.busy = true; sync();
      statusText('Recovering your cart…');
      try {
        const cart = await restoreCart();
        statusText(cart ? 'Cart restored from the server.' : 'No existing cart on this browser.');
      } catch (error) {
        statusText(error.code === 'cart_disabled' ? 'Cart API is disabled.' : error.message, true);
      } finally { state.busy = false; sync(); }
    });
    root.addEventListener('sd:quote-loading', () => { state.quote = null; clearApproval(); sync(); });
    root.addEventListener('sd:quote-error', () => { state.quote = null; clearApproval(); sync(); });
    root.addEventListener('sd:quote-ready', event => {
      state.quote = validQuote(event.detail);
      sync();
    });
    ['sd:artwork-selected', 'sd:artwork-cleared', 'sd:artwork-upload-error'].forEach(name => {
      root.addEventListener(name, () => { clearApproval(); sync(); });
    });
    root.addEventListener('sd:artwork-uploaded', () => { clearApproval(); sync(); });
    root.addEventListener('sd:studio-changed', event => {
      if (event.detail?.unsaved === true) clearApproval();
      sync();
    });
    sync();
    if (!uploadOn) statusText('Artwork upload is disabled. Cart testing cannot add projects.');
    else statusText(DEFAULT_MESSAGE);
    // Restoration is explicit, not automatic: avoids background 503s while API is off.
    count.textContent = '0';
    subtotal.textContent = showMoney(0);
  }
  function boot() { document.querySelectorAll('[data-sd-configurator]').forEach(install); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();